import React, { useEffect } from 'react';
import { Input, Quantity } from '@flixbus/honeycomb-react';

import { VoucherFormSection } from '../Section';

import { useVoucherFormContext } from '../Context';
import { Asterisk } from '../Asterisk';

interface VoucherFormStructureProps {
  template?: {
    prefix?: string;
    length?: number;
  };
}

const VoucherFormStructure: React.FC<VoucherFormStructureProps> = ({ template }) => {
  const MAX_LENGTH = 16;

  const {
    formState: {
      payload: { prefix, codeLength },
    },
    handleInputChange,
    setFormState,
  } = useVoucherFormContext();

  useEffect(() => {
    if (template) {
      setFormState(prev => ({
        ...prev,
        payload: {
          ...prev.payload,
          prefix: template?.prefix || '',
          codeLength: template?.length || 0,
        },
      }));
    }
  }, [setFormState, template]);

  const addLengthProps = {
    onClick: () => {
      if (MAX_LENGTH > codeLength) {
        updateLengthValue(codeLength + 1);
      }
    },
    disabled: codeLength >= MAX_LENGTH,
    'aria-label': 'Add code length',
  };

  const removeLengthProps = {
    onClick: () => {
      const newLength = codeLength - 1 < 1 ? 1 : codeLength - 1;
      updateLengthValue(newLength);
    },
    disabled: codeLength < 1,
    'aria-label': 'Remove code length',
  };

  const updateLengthValue = (value: number) => {
    setFormState(prev => ({
      ...prev,
      payload: {
        ...prev.payload,
        codeLength: value,
      },
    }));
  };

  const StructureInput = (
    <>
      <Input
        name='prefix'
        id='prefix'
        label={<>Prefix {!template?.prefix && <Asterisk />}</>}
        type='text'
        required
        disabled={template?.prefix ? true : false}
        value={template?.prefix || prefix}
        maxLength={MAX_LENGTH}
        {...(template?.prefix ? {} : { info: 'Must have less than 17 characters' })}
        onChange={e => {
          const regex = /^[ABCDEFGHJKLMNOPRSTUVWXYZ2346789]*$/;
          const inputValue = e.target.value.toUpperCase();

          if (regex.test(inputValue)) {
            handleInputChange(e, 'prefix');
          }
        }}
      />
      <Quantity
        label={<>Total length (Including prefix) {!template?.length && <Asterisk />}</>}
        id='prefix-quantity'
        name='prefix-quantity'
        disabled={template?.length ? true : false}
        value={template?.length || codeLength}
        minusBtnProps={removeLengthProps}
        plusBtnProps={addLengthProps}
        onChange={e => {
          const inputValue = parseInt(e.target.value, 10);
          if (!isNaN(inputValue) && inputValue >= 1 && inputValue <= MAX_LENGTH) {
            updateLengthValue(inputValue);
          }
        }}
      />
    </>
  );

  const inputs = <>{StructureInput}</>;

  return (
    <VoucherFormSection
      title='Voucher structure'
      subtitle='Prefix and total number of characters the voucher has.'
      inputs={inputs}
    />
  );
};

export default VoucherFormStructure;
